import { useState } from "react";
import { Card, CardContent } from "@e92/ui";
import { Chip } from "@e92/ui";
import { Badge } from "@e92/ui";
import { ThemeToggle } from "@e92/ui";
import "@e92/ui/styles.css";
import "./index.css";

type Theme = "light" | "dark" | "system";

const STACK = [
  "TypeScript", "React", "Bun", "Node.js",
  "WebRTC", "WebSockets", "WebCrypto",
  "SQLite", "Nginx", "Linux",
  "CSS", "WASM",
];

const PROJECTS = [
  {
    name: "e92/chat",
    url: "https://chat.e92.dev",
    desc: "End-to-end encrypted, real-time chat with WebRTC video calls, group conferences and a zero-trust server architecture. Built with Bun, React and WebCrypto.",
    badges: ["TypeScript", "Bun", "WebRTC", "E2EE"],
  },
  {
    name: "e92/ui",
    url: "https://ui.e92.dev",
    desc: "Open-source React component library with 100+ components, dark/light themes and zero runtime dependencies. Copy-paste or extend as you need.",
    badges: ["React", "TypeScript", "CSS"],
  },
];

export function App() {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem("e92-theme");
      if (stored === "light" || stored === "dark") return stored;
    } catch {}
    return "dark";
  });

  function handleTheme(next: Theme) {
    setTheme(next);
    const attr = next === "system" ? "dark" : next;
    document.documentElement.setAttribute("data-theme", attr);
    try { localStorage.setItem("e92-theme", attr); } catch {}
  }

  return (
    <div className="portfolio">
      <nav className="nav">
        <a href="/" className="nav-logo">e92<span>.dev</span></a>
        <div className="nav-links">
          <a href="https://chat.e92.dev" className="nav-link" target="_blank" rel="noopener noreferrer">chat</a>
          <a href="https://ui.e92.dev" className="nav-link" target="_blank" rel="noopener noreferrer">ui</a>
          <a href="https://github.com/e92" className="nav-link" target="_blank" rel="noopener noreferrer">github</a>
          <ThemeToggle theme={theme} onChange={handleTheme} />
        </div>
      </nav>

      <div className="page">
        <section className="hero">
          <span className="hero-eyebrow">Software Developer</span>
          <h1>Building things<br /><em>that matter.</em></h1>
          <p className="hero-sub">
            I build privacy-first tools and open-source software — from
            end-to-end encrypted chat to UI component libraries. Clean code,
            sharp interfaces, no nonsense.
          </p>
          <div className="hero-actions">
            <a href="https://github.com/e92" className="btn btn-default btn-lg" target="_blank" rel="noopener noreferrer">GitHub ↗</a>
            <a href="https://chat.e92.dev" className="btn btn-outline btn-lg" target="_blank" rel="noopener noreferrer">Try e92/chat</a>
            <a href="https://ui.e92.dev" className="btn btn-outline btn-lg" target="_blank" rel="noopener noreferrer">Browse e92/ui</a>
          </div>
        </section>

        <section className="section">
          <p className="section-label">Projects</p>
          <div className="projects">
            {PROJECTS.map((p) => (
              <a key={p.name} href={p.url} className="project-card-link" target="_blank" rel="noopener noreferrer">
                <Card>
                  <CardContent>
                    <div className="project-header">
                      <span className="project-name">{p.name}</span>
                      <div className="project-badges">
                        {p.badges.map((b) => <Badge key={b} variant="secondary">{b}</Badge>)}
                      </div>
                    </div>
                    <p className="project-desc">{p.desc}</p>
                    <span className="project-arrow">↗</span>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </section>

        <section className="section">
          <p className="section-label">Stack</p>
          <div className="stack-grid">
            {STACK.map((s) => <Chip key={s} variant="secondary" size="md">{s}</Chip>)}
          </div>
        </section>

        <section className="section">
          <p className="section-label">About</p>
          <p className="about-text">
            I&apos;m E92 — a software developer focused on building tools that respect
            users&apos; privacy and time. My work lives at the intersection of
            cryptography, real-time systems and clean UI design.
          </p>
          <p className="about-text">
            When I&apos;m not writing code, I&apos;m thinking about it. I care deeply
            about correctness, performance and interfaces that get out of the way —
            software should be invisible until you need it.
          </p>
        </section>

        <section className="section">
          <p className="section-label">Links</p>
          <div className="links-row">
            <a href="https://github.com/e92" className="btn btn-outline" target="_blank" rel="noopener noreferrer">GitHub ↗</a>
            <a href="https://chat.e92.dev" className="btn btn-outline" target="_blank" rel="noopener noreferrer">e92/chat ↗</a>
            <a href="https://ui.e92.dev" className="btn btn-outline" target="_blank" rel="noopener noreferrer">e92/ui ↗</a>
          </div>
        </section>
      </div>

      <footer className="footer">e92.dev — built with Bun &amp; React</footer>
    </div>
  );
}

export default App;
